/**
 * Estado del Plan de Producción por formato/semana.
 *
 * - `plan` es la matriz artículo → semana → unidades. Las filas son inmutables
 *   (se reemplazan al editar) para que el motor pueda hacer diffing por
 *   referencia y recalcular solo las hojas afectadas.
 * - El borrador se persiste en localStorage por empresa+año.
 */
import { create } from 'zustand'
import type { PlanMatrix } from '@/utils/planProduccion/engine'

const draftKey = (empresa: string, anio: number) => `airval-plan-draft-${empresa}-${anio}`

function loadDraft(empresa: string, anio: number): { exists: boolean; plan: PlanMatrix } {
  try {
    const raw = localStorage.getItem(draftKey(empresa, anio))
    return raw ? { exists: true, plan: JSON.parse(raw) as PlanMatrix } : { exists: false, plan: {} }
  } catch {
    return { exists: false, plan: {} }
  }
}

let saveTimer: ReturnType<typeof setTimeout> | undefined

function saveDraft(empresa: string, anio: number, plan: PlanMatrix) {
  clearTimeout(saveTimer)
  saveTimer = setTimeout(() => {
    try { localStorage.setItem(draftKey(empresa, anio), JSON.stringify(plan)) } catch {}
  }, 400)
}

interface PlanStore {
  /** Clave del borrador actualmente cargado ("empresa-anio"), '' si ninguno. */
  scope: string
  plan: PlanMatrix
  /** Formatos desplegados (nivel anidado), por clave compuesta "familia|formato". */
  expandedFormatos: Record<string, boolean>
  /** Familias desplegadas (nivel superior), por nombre de familia. */
  expandedFamilias: Record<string, boolean>
  expandedArticulos: Record<string, boolean>
  /** Semis desplegados, por ruta "articulo>semi[>semi…]" (independiente por artículo). */
  expandedSemis: Record<string, boolean>

  /** Carga (o restaura de localStorage) el plan editable de una empresa+año.
   *  Las OPs en curso NO se siembran aquí: se muestran como baseline read-only. */
  initScope: (empresa: string, anio: number) => void
  setCell: (empresa: string, anio: number, articleKey: string, weekIdx: number, qty: number | null) => void
  /** Reemplaza el plan completo (p. ej. "Proponer plan" desde la demanda). */
  setPlan: (empresa: string, anio: number, plan: PlanMatrix) => void
  clearPlan: (empresa: string, anio: number) => void
  toggleFormato: (formato: string) => void
  toggleFamilia: (famKey: string) => void
  toggleArticulo: (articleKey: string) => void
  toggleSemi: (pathKey: string) => void
  setAllFormatos: (formatos: string[], expanded: boolean) => void
  setAllFamilias: (famKeys: string[], expanded: boolean) => void
}

export const usePlanStore = create<PlanStore>((set, get) => ({
  scope: '',
  plan: {},
  expandedFormatos: {},
  expandedFamilias: {},
  expandedArticulos: {},
  expandedSemis: {},

  initScope: (empresa, anio) => {
    const scope = `${empresa}-${anio}`
    if (get().scope === scope) return
    const draft = loadDraft(empresa, anio)
    set({ scope, plan: draft.plan, expandedFormatos: {}, expandedFamilias: {}, expandedArticulos: {}, expandedSemis: {} })
  },

  setCell: (empresa, anio, articleKey, weekIdx, qty) => {
    set((state) => {
      const oldRow = state.plan[articleKey] ?? {}
      const newRow: Record<number, number> = { ...oldRow }
      if (qty === null || qty <= 0 || Number.isNaN(qty)) delete newRow[weekIdx]
      else newRow[weekIdx] = qty

      const plan: PlanMatrix = { ...state.plan }
      if (Object.keys(newRow).length === 0) delete plan[articleKey]
      else plan[articleKey] = newRow

      saveDraft(empresa, anio, plan)
      return { plan }
    })
  },

  setPlan: (empresa, anio, plan) => {
    saveDraft(empresa, anio, plan)
    set({ plan })
  },

  clearPlan: (empresa, anio) => {
    try { localStorage.setItem(draftKey(empresa, anio), '{}') } catch {}
    set({ plan: {} })
  },

  toggleFormato: (formato) =>
    set((s) => ({ expandedFormatos: { ...s.expandedFormatos, [formato]: !s.expandedFormatos[formato] } })),

  toggleFamilia: (famKey) =>
    set((s) => ({ expandedFamilias: { ...s.expandedFamilias, [famKey]: !s.expandedFamilias[famKey] } })),

  toggleArticulo: (articleKey) =>
    set((s) => ({ expandedArticulos: { ...s.expandedArticulos, [articleKey]: !s.expandedArticulos[articleKey] } })),

  toggleSemi: (pathKey) =>
    set((s) => ({ expandedSemis: { ...s.expandedSemis, [pathKey]: !s.expandedSemis[pathKey] } })),

  setAllFormatos: (formatos, expanded) =>
    set(() => ({ expandedFormatos: Object.fromEntries(formatos.map((f) => [f, expanded])) })),

  setAllFamilias: (famKeys, expanded) =>
    set(() => ({ expandedFamilias: Object.fromEntries(famKeys.map((k) => [k, expanded])) })),
}))
